/* global Drupal, once */

/* IMPORTS GO HERE */
import { navDesktop } from "../../templates/molecules/nav-desktop/display/nav-desktop.js";
import { navMobile } from "../../templates/organisms/header/display/mobile/mobile.js";
import { headerWrapper } from "../../templates/organisms/header-wrapper/display/header-wrapper.js";
import { memberGate } from "./member-gate.js";
import { externalLinks } from "./external-links.js";
import { documentLinks } from "./document-links.js";
import { getCookie } from "./_helpers.js";

/**
 * Initialises theme components found within a context.
 *
 * Runs on initial page load and again for any content Drupal inserts via
 * AJAX (e.g. Views exposed filters). Each component's elements are collected
 * with once() so they are only initialised a single time.
 *
 * @param {HTMLElement|Document} context
 *   The element to scope queries to (document on full page load, the
 *   inserted wrapper on AJAX updates).
 */
async function init(context) {
  /* Load JS for blocks only if they exist on the page */
  /* https://parceljs.org/features/code-splitting/ */

  // Image Carousel
  const imageCarousels = once(
    "ts-cswe-image-carousel",
    ".image-carousel",
    context,
  );
  if (imageCarousels.length > 0) {
    const imageCarouselJS =
      await import("../../templates/organisms/blocks/image-carousel/display/image-carousel.js");
    imageCarouselJS.render(imageCarousels);
  }

  // Spotlight Carousel
  const spotlightCarousels = once(
    "ts-cswe-spotlight-carousel",
    ".spotlight-carousel",
    context,
  );
  if (spotlightCarousels.length > 0) {
    const spotlightJS =
      await import("../../templates/organisms/blocks/spotlight-carousel/display/spotlight-carousel.js");
    spotlightJS.render(spotlightCarousels);
  }

  // Carousel
  const carousels = once(
    "ts-cswe-cards-carousel",
    ".cards-carousel .swiper",
    context,
  );
  if (carousels.length > 0) {
    const carouselJS =
      await import("../../templates/organisms/blocks/cards-content-carousel/display/cards-content-carousel.js");
    carouselJS.render(carousels);
  }

  /* Accordion */
  const accordionSections = once(
    "ts-cswe-accordion",
    ".accordion__section",
    context,
  );
  if (accordionSections.length > 0) {
    const accordionsJS =
      await import("../../templates/organisms/blocks/accordion/display/accordion.js");
    accordionsJS.render(accordionSections);
  }

  /* Program Search Result Cards */
  const programSearchToggles = once(
    "ts-cswe-program-search-card",
    ".card-program-search-listing__view-less",
    context,
  );
  if (programSearchToggles.length > 0) {
    const programSearchCardsJS =
      await import("../../templates/molecules/card-search-listing/display/card-programs-search-result.js");
    programSearchCardsJS.render(programSearchToggles);
  }

  /* Hero Image (extend flush to viewport edge on wide screens) */
  const heroImageColumns = once(
    "ts-cswe-hero-image",
    ".hero-image__image",
    context,
  );
  if (heroImageColumns.length > 0) {
    const heroImageJS =
      await import("../../templates/organisms/banners/hero-image/display/hero-image.js");
    heroImageJS.render(heroImageColumns);
  }

  /* Document links (PDF, Word, Excel, PowerPoint): say what file they open.
     Before externalLinks() only so the two notices read in that order —
     "Handbook (PDF) (opens in a new tab)". */
  documentLinks(context);

  /* Links that leave the page — off-site, or a document: open in a new tab,
     and announce that they do. */
  externalLinks(context);

  /* Error messages: a server-rendered one announces itself to nobody, so focus
     is moved to it. Matched on the tabindex the status-messages template puts on
     error messages only — core's JS-injected messages share the .messages--error
     hook but announce themselves correctly and must be left alone. */
  const errorMessages = once(
    "ts-cswe-status-message-focus",
    '[data-drupal-messages] .messages--error[tabindex="-1"]',
    context,
  );
  if (errorMessages.length > 0) {
    const statusMessageJS = await import("./status-message-focus.js");
    statusMessageJS.render(errorMessages);
  }

  /* Member soft-gate: reveal members-only content / wire the SSO sign-in. */
  memberGate(context);

  /* American Tradeshow registration links: for a signed-in member, append their
     Protech contact ID. Gated on the cookie first, so guests do no DOM query
     and never load the module. */
  const memberContactId = getCookie("cswe_protech_contact_id");
  if (memberContactId) {
    const registrationLinks = once(
      "ts-cswe-registration-links",
      'a[href^="https://registration.american-tradeshow.com/"]',
      context,
    );
    if (registrationLinks.length > 0) {
      const registrationLinksJS =
        await import("./member-registration-links.js");
      registrationLinksJS.render(registrationLinks, memberContactId);
    }
  }

  /* Table of Contents Desktop */
  const tocDesktop = once("ts-cswe-toc-desktop", ".toc-desktop", context);
  if (tocDesktop.length > 0) {
    const tocDesktopJS =
      await import("../../templates/organisms/blocks/table-of-contents/display/desktop/desktop.js");
    tocDesktopJS.render(tocDesktop);
  }

  /* Table of Contents Mobile */
  const tocMobile = once("ts-cswe-toc-mobile", ".toc-mobile", context);
  if (tocMobile.length > 0) {
    const tocMobileJS =
      await import("../../templates/organisms/blocks/table-of-contents/display/mobile/mobile.js");
    tocMobileJS.render(tocMobile);
  }

  /* Search Filters */
  const searchFilterButtons = once(
    "ts-cswe-search-filters",
    ".search-filter-button",
    context,
  );
  if (searchFilterButtons.length > 0) {
    const searchJS =
      await import("../../templates/organisms/search/display/search.js");
    searchJS.render(searchFilterButtons);
  }

  /* Search Filters — unpin the sticky panel when it outgrows the viewport. */
  const searchFilterPanels = once(
    "ts-cswe-search-sticky",
    ".search-filters",
    context,
  );
  if (searchFilterPanels.length > 0) {
    const searchStickyJS =
      await import("../../templates/organisms/search/display/search-sticky.js");
    searchStickyJS.render(searchFilterPanels);
  }

  /* Search Keyword Autocomplete */
  const searchAutocompleteInputs = once(
    "ts-cswe-search-autocomplete",
    "input.js-search-autocomplete",
    context,
  );
  if (searchAutocompleteInputs.length > 0) {
    const autocompleteJS =
      await import("../../templates/organisms/search/display/search-autocomplete.js");
    autocompleteJS.render(searchAutocompleteInputs);
  }

  /* Search Facet Selects — drop the Tagify-replaced selects from the a11y tree. */
  const facetSelects = once(
    "ts-cswe-search-facet-a11y",
    "select.tagify-select-widget",
    context,
  );
  if (facetSelects.length > 0) {
    const facetA11yJS =
      await import("../../templates/organisms/search/display/search-facet-a11y.js");
    facetA11yJS.render(facetSelects);
  }

  /* Back to Top */
  const backToTopBtns = once(
    "ts-cswe-back-to-top",
    ".btn-back-to-top",
    context,
  );
  if (backToTopBtns.length > 0) {
    const backToTopJS =
      await import("../../templates/molecules/back-to-top/display/back-to-top.js");
    backToTopJS.render(backToTopBtns);
  }

  /* Shopify deep link — only when a search result asked for a product.
     Gated on the parameter as well as the grid so the module is not fetched on
     an ordinary visit to the store, which is most of them. */
  if (new URLSearchParams(window.location.search).has("product")) {
    const buyButtonGrids = once(
      "ts-cswe-shopify-deep-link",
      '[id^="collection-component-"]',
      context,
    );
    if (buyButtonGrids.length > 0) {
      const shopifyDeepLinkJS =
        await import("../../templates/organisms/blocks/html-snippet/display/shopify-deep-link.js");
      shopifyDeepLinkJS.render(buyButtonGrids);
    }
  }

  /* Global Components — run once on the initial full-document attach only. */
  if (once("ts-cswe-global", "body", context).length > 0) {
    navDesktop();
    navMobile();

    const headerWrapperEl = document.querySelector(".header-wrapper");
    if (headerWrapperEl) headerWrapper(headerWrapperEl);
  }
}

/* Initialize site scripts on page load and after every Drupal AJAX update. */
Drupal.behaviors.tsCswe = {
  attach(context) {
    init(context);
  },
};
